import { styled } from '@mui/system';
import { Box, Button, Chip, TextField, Skeleton } from '@mui/material';
import CustomPaper from '../../common/CustomPaper';
import { keyframes } from '@mui/system';

const pulseAnimation = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 5px rgba(76, 175, 80, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`;

export const styles = () => ({
  root: {
    '& .jodit-container': {
      border: 'none !important',
      minHeight: 'unset!important',
      backgroundColor: 'transparent !important',
      borderRadius: '4px !important',
      boxShadow: 'none !important',
    },
    '& .jodit-wysiwyg': {
      padding: '6px !important',
      minHeight: 'unset!important',
      border: 'none !important',
      borderRadius: '4px !important',
      lineHeight: '1.4 !important',
    },
    '& .jodit-workplace': {
      padding: '0px!important',
      minHeight: 'unset!important',
    },
    '& a': {
      color: '#0066cc',
    },
    width: '100%',
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
  },
});

export const AppBarButton = styled(Button)({
  textTransform: 'none',
  color: 'rgba(134, 134, 134, 1)',
  fontSize: '14px',
  marginRight: '0.5rem',
});

export const StyledATag = styled('a')(({ theme }) => ({
  '&:hover': {
    textDecoration: 'underline !important',
  },
  color: theme.palette.text.primary,
  marginBottom: '4px',
  fontSize: '1.1em',
  textDecoration: 'none',
}));

export const StyledBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const AssignedOnSpan = styled('span')({
  fontSize: '0.9em',
  color: 'rgba(134, 134, 134, 1)',
});

export const StyledPaper = styled(CustomPaper)({
  width: '100%',
  padding: '1rem',
  borderRadius: '8px',
  background: '#FFF',
  boxShadow: 'none',
  border: 'none',
});

export const ExpandButton = styled(Button)({
  minWidth: 'auto',
  padding: '2px',
});

export const SectionContainer = styled(Box)({
  marginBottom: '10px',
});

export const SectionTitle = styled('p')({
  fontSize: '0.75rem',
  margin: 0,
  color: 'rgba(0, 0, 0, 0.6)',
  letterSpacing: '0.5px',
  fontWeight: 500,
});

export const ContentParagraph = styled('p')({
  marginTop: '4px',
  marginBottom: '8px',
  fontSize: '0.95rem',
  lineHeight: '1.4',
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  borderRadius: '4px',
  padding: '6px',
  boxShadow: 'none',
});

export const UserSection = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
  marginTop: '8px',
  marginBottom: '8px',
});

export const UserChip = styled(Chip)({
  borderRadius: '16px',
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
});

export const InfoCard = styled(Box)({
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  borderRadius: '6px',
  padding: '8px',
  marginBottom: '10px',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: 'none',
});

export const InfoItem = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  marginBottom: '6px',
  '&:last-child': {
    marginBottom: 0,
  },
});

export const InfoLabel = styled('span')({
  fontSize: '0.75rem',
  color: 'rgba(0, 0, 0, 0.6)',
  minWidth: '100px',
});

export const InfoValue = styled('span')({
  fontSize: '0.95rem',
  fontWeight: 500,
});

export const TimeTrackingContainer = styled(Box)({
  background: 'rgba(0, 0, 0, 0.02)',
  padding: '6px 8px',
  borderRadius: '6px',
  width: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  boxShadow: 'none',
  border: '1px solid rgba(0, 0, 0, 0.08)',
});

export const TimeUnit = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

export const TimeValue = styled('span')({
  fontSize: '1.2rem',
  fontWeight: 600,
  lineHeight: 1,
});

export const TimeLabel = styled('span')({
  fontSize: '0.7rem',
  color: 'rgba(0, 0, 0, 0.6)',
});

export const PulsingIndicator = styled('div')(() => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#4caf50',
  marginRight: '6px',
  animation: `${pulseAnimation} 1.5s infinite`,
}));

export const LiveTimeContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const TaskIdContainer = styled(Box)({
  marginTop: '4px',
  marginBottom: '8px',
  fontSize: '0.9rem',
  fontFamily: 'monospace',
  padding: '4px 6px',
  background: 'rgba(0, 0, 0, 0.02)',
  borderRadius: '4px',
  wordBreak: 'break-all',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: 'none',
});

export const TaskHeaderBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '0.5rem',
});

export const TaskTitleHeading = styled('h4')({
  marginLeft: '6px',
  lineHeight: '16px',
});

export const ContentBox = styled(Box)({
  padding: '0.75rem',
});

export const FormTextField = styled(TextField)({
  paddingBottom: '6px',
  marginTop: '4px',
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.08)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.2)',
    },
  },
});

export const EditorContainer = styled('div')({
  paddingBottom: '6px',
  marginTop: '4px',
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  borderRadius: '4px',
  padding: '6px',
  boxShadow: 'none',
});

export const NoAssigneesSpan = styled('span')({
  color: 'rgba(0, 0, 0, 0.5)',
  fontStyle: 'italic',
});

export const SkeletonContainer = styled(Skeleton)({
  marginBottom: '0.5rem',
});

export const ComponentNameParagraph = styled('p')({
  marginTop: '4px',
  marginBottom: '8px',
  fontSize: '0.95rem',
  fontWeight: 'bold',
  lineHeight: '1.4',
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  borderRadius: '4px',
  padding: '6px',
  boxShadow: 'none',
});

export const DescriptionContainer = styled('div')({
  marginTop: '4px',
  marginBottom: '8px',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  borderRadius: '4px',
  boxShadow: 'none',
  overflow: 'hidden',
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
});

export const TaskSubjectHeader = styled('h4')({
  marginLeft: '8px',
  lineHeight: '16px',
});

export const NoInformationMessage = styled('span')({
  color: 'rgba(0, 0, 0, 0.5)',
  fontStyle: 'italic',
});
